import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider, useSite } from "@/context/SiteContext";
import { AuthProvider } from "@/context/AuthContext";
import { usePageTracking } from "@/hooks/useSeo";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ChatWidget from "@/components/site/ChatWidget";
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import { About, Services, ServiceDetail, Projects, ProjectDetail, Process, Reviews, ServiceArea, ServiceAreas, NotFound } from "@/pages/Pages";
import AdminLayout, { RequireArea } from "@/admin/AdminLayout";
import { Login, Setup, TokenPassword } from "@/admin/AuthPages";
import { Dashboard, Analytics } from "@/admin/Dashboard";
import Leads from "@/admin/Leads";
import AITraining from "@/admin/AITraining";
import MediaLibrary from "@/admin/MediaLibrary";
import ContentAdmin, { SeoAdmin } from "@/admin/ContentAdmin";
import SettingsAdmin from "@/admin/SettingsAdmin";
import Help from "@/admin/Help";
import { ProjectsAdmin, ServicesAdmin, VideosAdmin, ReviewsAdmin, FaqsAdmin, ProcessAdmin, AreasAdmin } from "@/admin/Collections";

function Consent() {
  const { site } = useSite();
  const ga = site?.settings?.integrations_public?.ga4_id;
  const gsc = site?.settings?.integrations_public?.gsc_verification;
  const [choice, setChoice] = useState(() => localStorage.getItem("ib_consent"));
  useEffect(() => {
    if (gsc && !document.querySelector('meta[name="google-site-verification"]')) {
      const m = document.createElement("meta"); m.name = "google-site-verification"; m.content = gsc; document.head.appendChild(m);
    }
    if (ga && choice === "yes" && !window.gtag) {
      const s = document.createElement("script"); s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${ga}`; document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
      window.gtag("js", new Date()); window.gtag("config", ga);
    }
  }, [ga, gsc, choice]);
  if (!ga || choice) return null;
  const set = (v) => { localStorage.setItem("ib_consent", v); setChoice(v); };
  return (
    <div className="fixed bottom-24 left-4 z-50 max-w-sm bg-[color:var(--ib-charcoal)] p-4 text-sm text-white shadow-2xl sm:bottom-5" data-testid="consent-banner">
      We use analytics cookies to understand how visitors use our site.
      <div className="mt-3 flex gap-2"><button onClick={() => set("yes")} className="bg-[color:var(--ib-orange)] px-3 py-1.5" data-testid="consent-accept">Accept</button><button onClick={() => set("no")} className="border border-white/40 px-3 py-1.5" data-testid="consent-decline">Decline</button></div>
    </div>
  );
}

function PublicLayout() {
  usePageTracking();
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-3">Skip to content</a>
      <Header />
      <div id="main"><Outlet /></div>
      <Footer />
      <ChatWidget />
      <Consent />
    </>
  );
}

const A = (area, el) => <RequireArea area={area}>{el}</RequireArea>;

export default function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <AuthProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />
              <Route path="/process" element={<Process />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/service-areas/:slug" element={<ServiceArea />} />
              <Route path="*" element={<NotFound catchAll />} />
            </Route>
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/setup" element={<Setup />} />
            <Route path="/admin/reset-password" element={<TokenPassword mode="reset" />} />
            <Route path="/admin/accept-invite" element={<TokenPassword mode="invite" />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={A("leads", <Leads />)} />
              <Route path="ai" element={A("ai", <AITraining />)} />
              <Route path="projects" element={A("content", <ProjectsAdmin />)} />
              <Route path="photos" element={A("content", <MediaLibrary />)} />
              <Route path="videos" element={A("content", <VideosAdmin />)} />
              <Route path="services" element={A("content", <ServicesAdmin />)} />
              <Route path="content" element={A("content", <ContentAdmin />)} />
              <Route path="process" element={A("content", <ProcessAdmin />)} />
              <Route path="reviews" element={A("content", <ReviewsAdmin />)} />
              <Route path="faqs" element={A("content", <FaqsAdmin />)} />
              <Route path="areas" element={A("content", <AreasAdmin />)} />
              <Route path="seo" element={A("content", <SeoAdmin />)} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="settings" element={A("settings", <SettingsAdmin />)} />
              <Route path="help" element={<Help />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}
